import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiClock, FiX } from 'react-icons/fi';
import TimeSelector from './TimeSelector';
import './CustomTimePicker.css';

const CustomTimePicker = ({
    value,
    onChange,
    name,
    className = '',
    disabled = false,
    step,
    placeholder,
    required = false,
    minTime,
    maxTime,
    id,
}) => {
    const { t } = useTranslation('common');
    const resolvedPlaceholder = placeholder || t('timePicker.selectTime');

    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const minuteStep = step ? (step >= 60 ? Math.floor(step / 60) : step) : 15;

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 480);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const positionDropdown = useCallback(() => {
        if (!inputRef.current || isMobile) return;

        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 300;

        const openBelow = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

        setDropdownStyle({
            position: 'fixed',
            left: `${rect.left}px`,
            width: `${Math.max(rect.width, 200)}px`,
            ...(openBelow
                ? { top: `${rect.bottom + 4}px` }
                : { bottom: `${window.innerHeight - rect.top + 4}px` }),
            zIndex: 99999,
        });
    }, [isMobile]);

    const handleOpen = useCallback(() => {
        if (disabled) return;
        positionDropdown();
        setIsOpen(true);
    }, [disabled, positionDropdown]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleSelect = useCallback((timeStr) => {
        const syntheticEvent = {
            target: { name: name || '', value: timeStr },
            currentTarget: { name: name || '', value: timeStr },
        };
        onChange(syntheticEvent);
    }, [name, onChange]);

    const handleClear = useCallback((e) => {
        e.stopPropagation();
        const syntheticEvent = {
            target: { name: name || '', value: '' },
            currentTarget: { name: name || '', value: '' },
        };
        onChange(syntheticEvent);
    }, [name, onChange]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e) => {
            if (
                inputRef.current && !inputRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                handleClose();
            }
        };

        const handleEscape = (e) => {
            if (e.key === 'Escape') handleClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, handleClose]);

    useEffect(() => {
        if (isOpen && !isMobile) {
            positionDropdown();
            window.addEventListener('scroll', positionDropdown, true);
            window.addEventListener('resize', positionDropdown);
            return () => {
                window.removeEventListener('scroll', positionDropdown, true);
                window.removeEventListener('resize', positionDropdown);
            };
        }
    }, [isOpen, isMobile, positionDropdown]);

    const renderDropdown = () => {
        if (!isOpen) return null;

        const selector = (
            <TimeSelector
                value={value}
                onChange={handleSelect}
                step={minuteStep}
                minTime={minTime}
                maxTime={maxTime}
            />
        );

        if (isMobile) {
            return createPortal(
                <div className="ctp-picker-overlay" onClick={handleClose}>
                    <div
                        className="ctp-picker-sheet"
                        ref={dropdownRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ctp-picker-sheet-handle" />
                        <div className="ctp-picker-sheet-title">{t('timePicker.selectTime')}</div>
                        {selector}
                    </div>
                </div>,
                document.body
            );
        }

        return createPortal(
            <div ref={dropdownRef} className="ctp-picker-dropdown" style={dropdownStyle}>
                {selector}
            </div>,
            document.body
        );
    };

    return (
        <>
            <div
                ref={inputRef}
                className={`ctp-picker-input ${isOpen ? 'ctp-picker-input--open' : ''} ${disabled ? 'ctp-picker-input--disabled' : ''} ${className}`}
                onClick={handleOpen}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); } }}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                id={id}
            >
                <FiClock className="ctp-picker-icon" />
                <span className={`ctp-picker-text ${!value ? 'ctp-picker-text--placeholder' : ''}`}>
                    {value ? value.substring(0, 5) : resolvedPlaceholder}
                </span>
                {value && !required && !disabled && (
                    <button
                        type="button"
                        className="ctp-picker-clear"
                        onClick={handleClear}
                        aria-label={t('timePicker.clearTime')}
                        tabIndex={-1}
                    >
                        <FiX />
                    </button>
                )}
            </div>
            {renderDropdown()}
        </>
    );
};

export default CustomTimePicker;
